/**
 * Version Manager
 * Handles app version tracking while preserving browser drafts and queues,
 * and coordination with service worker updates
 */

interface VersionData {
  version: string;
  buildTime: string;
  gitHash: string;
  serverTime?: string;
}

interface StoredVersion {
  version: string;
  lastChecked: string;
}

const VERSION_KEY = 'app-version';
const VERSION_CHECK_INTERVAL = 60000; // Check every minute

class VersionManager {
  private currentVersion: VersionData | null = null;
  private checkInterval: number | null = null;
  private listeners: Array<(hasUpdate: boolean, newVersion?: VersionData) => void> = [];

  /**
   * Initialize version manager
   * Checks current version and sets up periodic checks
   */
  async initialize(): Promise<void> {
    console.log('[Version Manager] Initializing...');

    try {
      // Fetch current server version
      const serverVersion = await this.fetchServerVersion();
      this.currentVersion = serverVersion;

      // A release version is metadata, not proof that local business data is disposable.
      this.storeVersion(serverVersion.version);

      // Start periodic checks
      this.startPeriodicChecks();

      console.log('[Version Manager] Initialized with version:', serverVersion.version);
    } catch (error) {
      console.error('[Version Manager] Initialization failed:', error);
    }
  }

  /**
   * Fetch current version from server
   */
  async fetchServerVersion(): Promise<VersionData> {
    try {
      const response = await fetch('/api/version');
      const data = await response.json();

      if (data.success) {
        return {
          version: data.version,
          buildTime: data.buildTime,
          gitHash: data.gitHash,
          serverTime: data.serverTime,
        };
      } else {
        throw new Error('Failed to fetch version data');
      }
    } catch (error) {
      console.error('[Version Manager] Error fetching version:', error);
      // Return fallback version
      return {
        version: 'unknown',
        buildTime: new Date().toISOString(),
        gitHash: 'unknown',
      };
    }
  }

  /**
   * Store current version in localStorage
   */
  private storeVersion(version: string): void {
    const versionData: StoredVersion = {
      version,
      lastChecked: new Date().toISOString(),
    };
    localStorage.setItem(VERSION_KEY, JSON.stringify(versionData));
  }

  /**
   * Start periodic version checks
   */
  private startPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = window.setInterval(async () => {
      await this.checkForUpdates();
    }, VERSION_CHECK_INTERVAL);

    // Also check when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForUpdates();
      }
    });
  }

  /**
   * Check if a newer version is available
   */
  async checkForUpdates(): Promise<boolean> {
    try {
      const serverVersion = await this.fetchServerVersion();

      if (!this.currentVersion) {
        this.currentVersion = serverVersion;
        return false;
      }

      // Check if version changed
      if (serverVersion.version !== this.currentVersion.version) {
        console.log('[Version Manager] New version available:', {
          current: this.currentVersion.version,
          new: serverVersion.version,
        });

        // Notify listeners
        this.notifyListeners(true, serverVersion);

        return true;
      }

      return false;
    } catch (error) {
      console.error('[Version Manager] Error checking for updates:', error);
      return false;
    }
  }

  /**
   * Add listener for version updates
   */
  onUpdate(callback: (hasUpdate: boolean, newVersion?: VersionData) => void): () => void {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(hasUpdate: boolean, newVersion?: VersionData): void {
    this.listeners.forEach((callback) => {
      try {
        callback(hasUpdate, newVersion);
      } catch (error) {
        console.error('[Version Manager] Error in listener callback:', error);
      }
    });
  }

  /**
   * Get current version
   */
  getCurrentVersion(): VersionData | null {
    return this.currentVersion;
  }

  /**
   * Force reload the app
   */
  reloadApp(): void {
    console.log('[Version Manager] Reloading app...');
    window.location.reload();
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.listeners = [];
  }
}

// Export singleton instance
export const versionManager = new VersionManager();
